import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SectionCard, EmptyState, Pill, ActionButton, ProgressBar, WorkflowPipeline } from "@/components/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PhoneCallMenu } from "@/components/PhoneCallMenu";
import HistoricalConversations from "./HistoricalConversations";

const fmtDT = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const timeAgo = (d) => {
  if (!d) return "—";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function sourceFor(sender_type, channel) {
  if (sender_type === "penny") return { label: "AI", tone: "purple", icon: "Sparkles" };
  if (sender_type === "staff_note") return { label: "Note", tone: "warning", icon: "StickyNote" };
  if (sender_type === "system") return { label: "System", tone: "neutral", icon: "Info" };
  const ch = (channel || "").toLowerCase();
  if (ch === "email") return { label: "Email", tone: "info", icon: "Mail" };
  if (ch === "sms") return { label: "SMS", tone: "info", icon: "MessageSquare" };
  if (ch === "phone" || ch === "voice") return { label: "Phone", tone: "success", icon: "Phone" };
  return { label: "Portal", tone: "success", icon: "MessageCircle" };
}

function SkeletonLine({ w = "w-full" }) {
  return <div className={cn("h-3 rounded bg-slate-100 animate-pulse", w)} />;
}

function AttachmentInline({ att, isStaff = true }) {
  const [imgError, setImgError] = useState(false);
  const isImage = (att.content_type || "").startsWith("image/");
  const authSuffix = isStaff ? `?auth=${encodeURIComponent(getToken() || "")}` : "";
  const viewUrl = att.viewer_path
    ? (isStaff ? `${API}${att.viewer_path}${authSuffix}` : att.viewer_path)
    : null;
  const dlUrl = att.download_path
    ? (isStaff
        ? `${API}${att.download_path}${att.download_path.includes("?") ? "&" : "?"}auth=${encodeURIComponent(getToken() || "")}`
        : att.download_path)
    : null;

  if (isImage && !imgError && viewUrl) {
    return (
      <div className="mt-1.5" data-testid={`conv-att-image-${att.id}`}>
        <a href={viewUrl} target="_blank" rel="noopener noreferrer" aria-label={`View image: ${att.filename}`}>
          <img
            src={viewUrl}
            alt={att.filename}
            onError={() => setImgError(true)}
            className="max-w-[320px] max-h-[240px] rounded-lg border border-slate-200 object-contain cursor-pointer hover:opacity-90 transition-opacity"
          />
        </a>
        {dlUrl && (
          <a href={dlUrl} className="text-[10px] text-slate-400 hover:text-slate-600 mt-0.5 inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded" download={att.filename}>
            <Icons.Download className="h-3 w-3" />Download
          </a>
        )}
      </div>
    );
  }
  return (
    <a href={dlUrl || viewUrl || "#"} target="_blank" rel="noopener noreferrer" download={!isImage ? att.filename : undefined}
      data-testid={`conv-att-chip-${att.id}`}
      className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 transition-colors mt-1">
      <Icons.Paperclip className="h-3 w-3 shrink-0" />
      <span className="truncate max-w-[160px] font-medium" title={att.filename}>{att.filename}</span>
      <Icons.Download className="h-3 w-3 text-slate-400 shrink-0" />
    </a>
  );
}

function TimelineEvent({ ev }) {
  const [open, setOpen] = useState(false);
  const Icon = Icons[ev.icon] || Icons.Circle;
  const expandable = ev.body && ev.body.length > 140;
  const shown = open || !expandable ? ev.body : `${ev.body.slice(0, 140)}…`;
  const hasAttachments = ev.attachments && ev.attachments.length > 0;
  return (
    <div className="flex gap-3" data-testid={`conv-event-${ev.key}`}>
      <div className="flex flex-col items-center">
        <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
          ev.right ? "bg-charcoal text-white" : "bg-slate-100 text-slate-500")}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex-1 w-px bg-slate-100 my-1" />
      </div>
      <div className="min-w-0 flex-1 pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800">{ev.title}</span>
          <Pill tone={ev.source.tone} className="!px-1.5 !py-0 text-[10px]">{ev.source.label}</Pill>
          <span className="text-[11px] text-slate-400">{fmtDT(ev.ts)}</span>
        </div>
        {ev.body && <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap break-words">{shown}</div>}
        {expandable && (
          <button onClick={() => setOpen((v) => !v)} data-testid={`conv-event-toggle-${ev.key}`}
            className="text-xs font-medium text-slate-500 hover:text-charcoal mt-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{open ? "Show less" : "Show more"}</button>
        )}
        {hasAttachments && (
          <div className="mt-1.5 flex flex-wrap gap-2" data-testid={`conv-event-attachments-${ev.key}`}>
            {ev.attachments.map((att) => <AttachmentInline key={att.id} att={att} isStaff />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CtxSection({ title, icon, children, action }) {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="border-b border-slate-100 last:border-0 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <Icon className="h-3.5 w-3.5" />{title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function CtxLink({ onNavigate, to }) {
  return (
    <button onClick={() => onNavigate?.(to)} className="text-xs text-slate-400 hover:text-charcoal rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">View</button>
  );
}

function ContextBody({ c, meetings, tasks, recordings, uploaded, wf, estimates, tax, onNavigate }) {
  const upcoming = meetings.filter((m) => new Date(m.scheduled_start) >= new Date());
  return (
    <>
      <CtxSection title="Contact" icon="Contact">
        <div className="space-y-1.5 text-sm">
          <div className="text-slate-700 truncate">{c.email || "No email"}</div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              {c.mobile_phone && <div className="text-slate-500 truncate" data-testid="conv-contact-mobile">Mobile: {c.mobile_phone}</div>}
              {c.home_phone && <div className="text-slate-500 truncate" data-testid="conv-contact-home">Home: {c.home_phone}</div>}
              {!c.mobile_phone && !c.home_phone && <div className="text-slate-400">No phone</div>}
            </div>
            <PhoneCallMenu options={c.phone_options || []} testid="conv-contact-call" />
          </div>
        </div>
      </CtxSection>
      <CtxSection title="Upcoming Meetings" icon="Calendar" action={<CtxLink onNavigate={onNavigate} to="meetings" />}>
        {upcoming.length === 0 ? <p className="text-xs text-slate-400">None scheduled.</p> :
          upcoming.slice(0, 3).map((m) => (
            <div key={m.id} className="text-sm text-slate-700 flex justify-between gap-2"><span className="truncate">{m.title}</span><span className="text-xs text-slate-400 shrink-0">{fmtDate(m.scheduled_start)}</span></div>
          ))}
      </CtxSection>
      <CtxSection title="Open Tasks" icon="CheckSquare" action={<CtxLink onNavigate={onNavigate} to="tasks" />}>
        {tasks.length === 0 ? <p className="text-xs text-slate-400">No open tasks.</p> :
          tasks.slice(0, 4).map((t) => <div key={t.id} className="text-sm text-slate-700 truncate">• {t.title}</div>)}
      </CtxSection>
      <CtxSection title="Voice Mail" icon="Voicemail">
        {recordings.length === 0 ? <p className="text-xs text-slate-400">No recordings.</p> :
          recordings.slice(0, 3).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm"><span className="text-slate-600">{fmtDate(r.recorded_at)}</span><span className="text-[11px] text-slate-400">{r.duration}</span></div>
          ))}
      </CtxSection>
      <CtxSection title="Attachments" icon="Paperclip" action={<CtxLink onNavigate={onNavigate} to="documents" />}>
        {uploaded.length === 0 ? <p className="text-xs text-slate-400">No files uploaded.</p> :
          uploaded.slice(0, 4).map((f) => <div key={f.id} className="text-sm text-slate-700 truncate">• {f.name || f.filename || f.original_filename || f.title || (f.document_type || "").replace(/_/g, " ") || "Document"}</div>)}
      </CtxSection>
      <CtxSection title="Workflow" icon="GitMerge" action={<CtxLink onNavigate={onNavigate} to="workflow" />}>
        {wf?.pipeline ? <WorkflowPipeline stages={wf.pipeline.stages} currentStage={wf.state.current_stage} history={wf.state.stage_history} percent={wf.state.percent} /> : <p className="text-xs text-slate-400">No active workflow.</p>}
      </CtxSection>
      <CtxSection title="Recent Estimates" icon="Calculator" action={<CtxLink onNavigate={onNavigate} to="estimates" />}>
        {estimates.length === 0 ? <p className="text-xs text-slate-400">No estimates.</p> :
          estimates.slice(0, 3).map((e) => <div key={e.id} className="flex justify-between text-sm"><span className="font-mono text-xs text-slate-500">{e.estimate_number}</span><span className="text-slate-700">${(e.total || 0).toLocaleString()}</span></div>)}
      </CtxSection>
      <CtxSection title="Tax Profile" icon="FileUser" action={<CtxLink onNavigate={onNavigate} to="tax" />}>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Residency</span><span className="text-slate-700">{tax.tax_residency || "—"}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Filing</span><span className="text-slate-700">{tax.filing_status || "—"}</span></div>
        </div>
      </CtxSection>
    </>
  );
}

export default function ConversationWorkspace({ clientId, bundle, related, timeline = [], documents, reload, onNavigate, openComposer, onOpenComposerHandled }) {
  const navigate = useNavigate();
  const [comm, setComm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("reply"); // reply | email | note
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(() => {
    try { return localStorage.getItem("tf.ws.ctxCollapsed") !== "1"; } catch { return true; }
  });
  const [ctxDrawerOpen, setCtxDrawerOpen] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{uid, docId, name, size, type, status, error}]
  const composerRef = useRef(null);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerSessionRef = useRef(null); // draft correlation ID — lazy-init on first file add

  const fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const uploadFile = useCallback(async (file) => {
    if (!composerSessionRef.current) {
      composerSessionRef.current = Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    const uid = Math.random().toString(36).slice(2);
    // For image types, create a local preview URL so the chip can show a thumbnail
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    setAttachments((prev) => [...prev, { uid, docId: null, name: file.name, size: file.size, type: file.type, status: "uploading", error: null, previewUrl, _file: file }]);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("draft_correlation_id", composerSessionRef.current);
    try {
      const { data } = await api.post("/communications/attachment-draft", fd);
      setAttachments((prev) => prev.map((a) => a.uid === uid ? { ...a, docId: data.id, status: "ready" } : a));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : "Upload failed";
      setAttachments((prev) => prev.map((a) => a.uid === uid ? { ...a, status: "failed", error: errMsg } : a));
    }
  }, [clientId]);

  const onFilesSelected = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => uploadFile(f));
    e.target.value = "";
  }, [uploadFile]);

  // Clipboard paste: intercept image items only; text paste continues unchanged
  const onComposerPaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const SUPPORTED = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const imageItems = items.filter((item) => SUPPORTED.includes(item.type));
    if (imageItems.length === 0) return; // no image — let browser handle normally
    e.preventDefault();
    imageItems.forEach((item) => {
      const blob = item.getAsFile();
      if (!blob) return;
      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
      const ext = item.type === "image/png" ? "png" : item.type === "image/webp" ? "webp" : "jpg";
      const file = new File([blob], `Screenshot-${ts}.${ext}`, { type: item.type });
      uploadFile(file);
    });
    // If clipboard also had text, paste that too (preserve both)
    const textItem = items.find((item) => item.kind === "string" && item.type === "text/plain");
    if (textItem) {
      textItem.getAsString((txt) => {
        if (txt) setDraft((prev) => prev + txt);
      });
    }
  }, [uploadFile]);

  const removeAttachment = useCallback((uid) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.uid === uid);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((a) => a.uid !== uid);
    });
  }, []);

  const retryAttachment = useCallback((uid) => {
    const attSnap = attachments.find((a) => a.uid === uid);
    if (!attSnap) return;
    if (attSnap.previewUrl) URL.revokeObjectURL(attSnap.previewUrl);
    setAttachments((prev) => prev.filter((a) => a.uid !== uid));
    if (attSnap._file) {
      uploadFile(attSnap._file);
    } else {
      toast.info("Please re-attach the file to retry.");
    }
  }, [attachments, uploadFile]);

  useEffect(() => {
    try { localStorage.setItem("tf.ws.ctxCollapsed", ctxOpen ? "0" : "1"); } catch { /* ignore */ }
  }, [ctxOpen]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/workspace/${clientId}/communications`); setComm(data); }
    catch (e) { console.warn("Failed to load workspace communications", e); } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Initial-load auto-scroll intentionally removed.
  // Events are sorted newest-first so the most recent content is already at the top.
  // openComposer effect below handles the Send Message composer focus independently.

  // Open composer deep-link: triggered from Actions → Send Message
  useEffect(() => {
    if (!openComposer) return;
    onOpenComposerHandled?.(); // reset immediately to prevent re-fire
    setMode("reply");
    // Allow tab-switch animation to complete before scrolling + focusing
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      setTimeout(() => { composerRef.current?.focus(); }, 150);
    }, 120);
  }, [openComposer]); // eslint-disable-line react-hooks/exhaustive-deps

  const intel = bundle?.intelligence || {};
  const tax = bundle?.tax_profile || {};
  const c = bundle?.contact || {};
  const wf = bundle?.workflow;
  const meta = comm?.meta || {};
  const messages = comm?.messages || [];
  const recordings = comm?.recordings || [];
  const meetings = related?.meetings || [];
  const tasks = (related?.tasks || []).filter((t) => !t.completed_at);
  const estimates = related?.estimates || [];
  const uploaded = documents?.uploaded_files || [];

  const lastClient = useMemo(() => [...messages].reverse().find((m) => m.sender_type === "client"), [messages]);
  const lastMsg = messages[messages.length - 1];
  const waitingOnStaff = lastMsg && lastMsg.sender_type === "client";

  // Unified timeline: newest-first (most recent activity at top)
  const events = useMemo(() => {
    const out = [];
    messages.forEach((m) => {
      const src = sourceFor(m.sender_type, m.channel);
      out.push({
        key: `m-${m.id}`, ts: m.sent_at, source: src, icon: src.icon,
        right: m.sender_type === "staff" || m.sender_type === "staff_note",
        title: m.sender_name || src.label, body: m.body,
        attachments: m.attachments || [],
      });
    });
    recordings.forEach((r) => out.push({
      key: `r-${r.id}`, ts: r.recorded_at, source: { label: "Voice Mail", tone: "warning", icon: "Voicemail" },
      icon: "Voicemail", title: "Voice mail", body: `${r.duration || ""}${r.has_transcript ? " · transcript available" : ""}`,
    }));
    meetings.forEach((mt) => out.push({
      key: `mt-${mt.id}`, ts: mt.scheduled_start, source: { label: "Meeting", tone: "info", icon: "Calendar" },
      icon: "Calendar", title: mt.title || "Meeting", body: `${(mt.type || "").replace(/_/g, " ")}${mt.status ? ` · ${mt.status}` : ""}`,
    }));
    (timeline || []).forEach((t) => {
      const isWf = /workflow|stage|engagement|estimate|proposal/i.test(`${t.title} ${t.description || ""}`);
      const isAi = /allison|ai |analysis|penny/i.test(`${t.title} ${t.description || ""}`);
      if (!isWf && !isAi) return;
      out.push({
        key: `t-${t.id}`, ts: t.created_at,
        source: isAi ? { label: "AI", tone: "purple", icon: "Sparkles" } : { label: "Workflow", tone: "neutral", icon: "GitMerge" },
        icon: isAi ? "Sparkles" : "GitMerge", title: t.title, body: t.description,
      });
    });
    // Newest first — most recent activity at top; stable secondary key for equal timestamps
    return out.filter((e) => e.ts).sort((a, b) => new Date(b.ts) - new Date(a.ts) || (a.key < b.key ? 1 : -1));
  }, [messages, recordings, meetings, timeline]);

  const focusComposer = useCallback((m = "reply") => {
    setMode(m);
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      setTimeout(() => { composerRef.current?.focus(); }, 60);
    }, 80);
  }, []);

  // Canonical send — routes through communications endpoints for proper side effects:
  // portal notification, client email, timeline event, audit, unread badge.
  // Internal notes are staff-only: no client email, no portal notification.
  const send = async () => {
    if (!draft.trim() || sending) return;
    if (attachments.some((a) => a.status === "uploading")) {
      toast.error("Please wait for all files to finish uploading.");
      return;
    }
    const failedAttachments = attachments.filter((a) => a.status === "failed");
    if (failedAttachments.length) {
      toast.error("Remove or retry failed attachments before sending.");
      return;
    }
    setSending(true);
    try {
      let convId = comm?.active_conversation?.id;

      // First message for this client: create conversation canonically first
      if (!convId) {
        const { data } = await api.post(`/communications/conversations`, {
          client_id: clientId,
          subject: "Conversation",
          channel: mode === "email" ? "email" : "portal",
          conversation_type: "staff_conversation",
        });
        convId = data.conversation?.id;
        if (!convId) throw new Error("Failed to create conversation");
      }

      const readyDocIds = attachments.filter((a) => a.status === "ready" && a.docId).map((a) => a.docId);

      if (mode === "note") {
        // Internal note — staff-only (endpoint-authoritative), no client notification, no email
        await api.post(`/communications/conversations/${convId}/internal-note`, {
          body: draft,
          attachment_document_ids: readyDocIds,
          draft_correlation_id: composerSessionRef.current,
        });
      } else {
        // Portal or email reply — canonical path with email, timeline, audit, unread badge
        await api.post(`/communications/conversations/${convId}/reply`, {
          body: draft,
          channel: mode === "email" ? "email" : "portal",
          attachment_document_ids: readyDocIds,
          draft_correlation_id: composerSessionRef.current,
        });
      }

      setDraft(""); // clear only on success
      setAttachments([]);
      composerSessionRef.current = null;
      toast.success(mode === "note" ? "Internal note added" : "Message sent");
      await load();
      reload?.();
      setTimeout(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : (detail?.message || "Could not send");
      toast.error(errMsg);
      // draft + attachments intentionally preserved on failure
    } finally {
      setSending(false);
    }
  };

  const summaryText = messages.length === 0
    ? "No communication yet. Start the conversation with a secure portal message."
    : `${messages.length} message${messages.length === 1 ? "" : "s"} in this conversation. ${lastClient ? `Last client message ${timeAgo(lastClient.sent_at)}.` : "No client message yet."} ${waitingOnStaff ? "Waiting on a staff reply." : "No reply is currently required."}`;

  return (
    <div className="flex gap-5 items-start" data-testid="conversation-workspace">

      {/* ── Main column — conversation-first flex layout ── */}
      <div className="flex-1 min-w-0 flex flex-col gap-4">

        {/* Scrollable content: header + allison brief + timeline + historical */}
        <div className="flex flex-col gap-4 pb-[400px]">

          {/* 1 — Conversation Header */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="conv-header">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-10 w-10 rounded-xl bg-charcoal text-white flex items-center justify-center shrink-0"><Icons.MessagesSquare className="h-5 w-5" /></span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-slate-900">Conversation</h3>
                    <Pill tone={waitingOnStaff ? "warning" : "success"} data-testid="conv-status">{waitingOnStaff ? "Waiting on Staff" : "Open"}</Pill>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Last client activity: <span className="font-medium text-slate-700" data-testid="conv-last-activity">{lastClient ? timeAgo(lastClient.sent_at) : "—"}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-center"><div className="text-[10px] uppercase tracking-wide text-slate-400">Assigned</div><div className="text-sm font-medium text-slate-800">{meta.assigned_to || "—"}</div></div>
                <div className="text-center"><div className="text-[10px] uppercase tracking-wide text-slate-400">Response SLA</div><div className="text-sm font-medium text-slate-800">{meta.response_sla_minutes || 15}m</div></div>
                <div className="text-center"><div className="text-[10px] uppercase tracking-wide text-slate-400">Unread</div><div className="text-sm font-medium text-slate-800" data-testid="conv-unread">{intel.unread_messages || 0}</div></div>
                <button onClick={() => setCtxDrawerOpen(true)} data-testid="conv-ctx-open-mobile" aria-label="Open context panel"
                  className="xl:hidden inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                  <Icons.PanelRight className="h-3.5 w-3.5" />Context
                </button>
              </div>
            </div>
          </div>

          {/* 2 — Allison Brief (collapsed by default, expandable) */}
          <div className="rounded-2xl border border-slate-200 border-l-4 border-l-brand-gold bg-white shadow-sm" data-testid="conv-allison-summary">
            {/* Always-visible header */}
            <div className="flex items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-slate-700 to-charcoal flex items-center justify-center shrink-0"><Icons.Sparkles className="h-4 w-4 text-brand-gold" /></span>
                <div className="min-w-0">
                  <div className="text-sm font-heading font-semibold text-slate-900">Allison · Conversation Brief</div>
                  {!briefExpanded && (
                    <div className="text-xs text-slate-500 truncate" data-testid="conv-brief-collapsed-summary">
                      {intel.next_best_action?.title || (waitingOnStaff ? "Reply to the client" : "No action required")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ActionButton icon="Reply" onClick={() => focusComposer("reply")} data-testid="conv-action-reply">Reply</ActionButton>
                <button
                  onClick={() => setBriefExpanded((v) => !v)}
                  data-testid="conv-brief-toggle"
                  aria-label={briefExpanded ? "Collapse Allison Brief" : "Expand Allison Brief"}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold transition-colors"
                >
                  {briefExpanded ? <Icons.ChevronUp className="h-4 w-4" /> : <Icons.ChevronDown className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Expanded content */}
            {briefExpanded && (
              <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                <p className="text-xs text-slate-400 mb-3">Reviewed the conversation — here's what matters most and the recommended next action.</p>
                {loading ? <div className="space-y-2"><SkeletonLine /><SkeletonLine w="w-2/3" /></div> : (
                  <>
                    <p className="text-sm text-slate-700 mb-3" data-testid="conv-exec-summary">{summaryText}</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-4">
                      <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Allison Recommends</div>
                        <div className="text-sm font-medium text-slate-800">{intel.next_best_action?.title || (waitingOnStaff ? "Reply to the client" : "No action required")}</div>
                        {intel.next_best_action?.description && <div className="text-xs text-slate-500 mt-0.5">{intel.next_best_action.description}</div>}
                      </div>
                      <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Confidence</div>
                        <ProgressBar value={Math.round((meta.penny_confidence || 0.9) * 100)} tone="emerald" showLabel />
                        <div className="text-xs text-slate-500 mt-1 capitalize">Sentiment: {meta.sentiment || "neutral"}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ActionButton icon="FileText" variant="secondary" onClick={() => onNavigate?.("documents")} data-testid="conv-action-docs">Request Documents</ActionButton>
                      <ActionButton icon="Calendar" variant="secondary" onClick={() => toast.info("Scheduling opens from the Meetings section")} data-testid="conv-action-meeting">Schedule Meeting</ActionButton>
                      <ActionButton icon="Calculator" variant="secondary" onClick={() => navigate(`/sales/estimates/new?client=${clientId}`)} data-testid="conv-action-estimate">Create Estimate</ActionButton>
                      <ActionButton icon="PanelRightOpen" variant="ghost" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="conv-action-briefing">View Full Briefing</ActionButton>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 3 — Activity Timeline (primary content) */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 px-4 pt-4 pb-2 border-b border-slate-100">
              <Icons.Activity className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-heading font-semibold text-slate-800">Activity Timeline</span>
            </div>
            <div className="p-4 min-h-[420px]">
              {loading ? (
                <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="flex gap-3"><div className="h-8 w-8 rounded-lg bg-slate-100 animate-pulse" /><div className="flex-1 space-y-2"><SkeletonLine w="w-1/3" /><SkeletonLine /></div></div>)}</div>
              ) : events.length === 0 ? (
                <EmptyState icon="MessageSquare" title="No communication yet"
                  description="The conversation will appear here once communication begins."
                  action={<ActionButton icon="Send" onClick={() => focusComposer("reply")} data-testid="conv-empty-send">Send Portal Message</ActionButton>} />
              ) : (
                <div data-testid="conv-timeline">{events.map((ev) => <TimelineEvent key={ev.key} ev={ev} />)}</div>
              )}
              {/* Scroll target — newest message is above this, composer is below */}
              <div ref={endRef} className="h-0" aria-hidden="true" data-testid="conv-end-anchor" />
            </div>
          </div>

          {/* 4 — Historical Conversations */}
          <HistoricalConversations clientId={clientId} />

        </div>{/* end content */}

        {/* 5 — Composer — sticky at page bottom (only overlays workspace content, not other tabs) */}
        <div
          className="sticky bottom-0 z-20 rounded-xl border border-slate-200 bg-white shadow-[0_-4px_24px_-6px_rgba(0,0,0,0.07)] p-4"
          data-testid="conv-composer"
        >
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            {[["reply", "Reply", "MessageCircle"], ["email", "Email", "Mail"], ["note", "Internal Note", "StickyNote"]].map(([m, l, ic]) => {
              const Icon = Icons[ic];
              return (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`conv-mode-${m}`}
                  className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold",
                    mode === m ? "bg-charcoal text-white" : "text-slate-500 hover:bg-slate-100")}>
                  <Icon className="h-3.5 w-3.5" />{l}
                </button>
              );
            })}
          </div>
          <textarea
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}
            onPaste={onComposerPaste}
            rows={10}
            aria-label={mode === "note" ? "Internal note" : "Reply message"}
            placeholder={mode === "note" ? "Add a private internal note… (paste screenshots with Ctrl+V)" : mode === "email" ? "Write an email to the client…" : "Write a secure portal reply… (paste screenshots with Ctrl+V)"}
            data-testid="conv-composer-input"
            className={cn(
              "w-full resize-none rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold min-h-[260px] max-h-[55vh] overflow-y-auto",
              mode === "note" ? "bg-amber-50 border border-amber-200" : "bg-white border border-slate-300"
            )}
          />
          {/* Attachment chips — shown when files are attached */}
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" role="list" aria-label="Attached files">
              {attachments.map((att) => (
                <div
                  key={att.uid}
                  role="listitem"
                  data-testid={`conv-attachment-chip-${att.uid}`}
                  className={cn(
                    "relative rounded-md border text-[11px] max-w-[200px]",
                    att.status === "uploading" && "border-blue-200 bg-blue-50",
                    att.status === "ready" && "border-green-200 bg-green-50",
                    att.status === "failed" && "border-red-200 bg-red-50"
                  )}
                >
                  {/* Image thumbnail preview for pasted screenshots */}
                  {att.previewUrl && att.status !== "failed" ? (
                    <div className="relative">
                      <img
                        src={att.previewUrl}
                        alt={att.name}
                        className="w-[120px] h-[80px] object-cover rounded-md"
                      />
                      {att.status === "uploading" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-md">
                          <Icons.Loader2 className="h-5 w-5 animate-spin text-white" />
                        </div>
                      )}
                      <div className="px-2 py-0.5 flex items-center justify-between gap-1">
                        <span className="truncate font-medium text-[10px] text-slate-600" title={att.name}>{att.name}</span>
                        <button type="button" onClick={() => removeAttachment(att.uid)}
                          aria-label={`Remove ${att.name}`}
                          data-testid={`conv-attachment-remove-${att.uid}`}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <Icons.X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("inline-flex items-center gap-1.5 px-2 py-1",
                      att.status === "uploading" && "text-blue-700",
                      att.status === "ready" && "text-green-800",
                      att.status === "failed" && "text-red-700"
                    )}>
                      {att.status === "uploading" ? (
                        <Icons.Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
                      ) : att.status === "ready" ? (
                        <Icons.Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
                      ) : (
                        <Icons.AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                      )}
                      <span className="truncate font-medium" title={att.name}>{att.name}</span>
                      <span className="text-[10px] opacity-70 shrink-0">
                        {att.status === "uploading" ? "Uploading…" : att.status === "ready" ? fmtSize(att.size) : "Failed"}
                      </span>
                      {att.status === "failed" && (
                        <button type="button" onClick={() => retryAttachment(att.uid)}
                          aria-label={`Retry uploading ${att.name}`}
                          data-testid={`conv-attachment-retry-${att.uid}`}
                          className="text-red-500 hover:text-red-700 shrink-0">
                          <Icons.RefreshCw className="h-3 w-3" aria-hidden="true" />
                        </button>
                      )}
                      <button type="button" onClick={() => removeAttachment(att.uid)}
                        aria-label={`Remove ${att.name}`}
                        data-testid={`conv-attachment-remove-${att.uid}`}
                        className="text-slate-400 hover:text-slate-600 shrink-0">
                        <Icons.X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Hidden file input — triggered by paperclip button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.odt,.ppt,.pptx"
            onChange={onFilesSelected}
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => fileInputRef.current?.click()} title="Attach files to this message" aria-label="Attach files to this message" data-testid="conv-attach" className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"><Icons.Paperclip className="h-4 w-4" /></button>
              <button type="button" onClick={() => onNavigate?.("tax")} title="Knowledge / Tax Profile" aria-label="Open knowledge and tax profile" data-testid="conv-knowledge" className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"><Icons.BookOpen className="h-4 w-4" /></button>
              <span className="text-xs text-slate-400 ml-1">Ctrl+Enter to send</span>
            </div>
            <ActionButton icon="Send" loading={sending} onClick={send} data-testid="conv-send" disabled={sending || attachments.some((a) => a.status === "uploading")}>{mode === "note" ? "Save Note" : "Send"}</ActionButton>
          </div>
        </div>

      </div>{/* end main column */}

      {/* 6 — Context Panel (inline on xl, drawer below xl) */}
      {ctxOpen ? (
        <aside className="hidden xl:block w-80 shrink-0" data-testid="conv-context-panel">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 sticky top-[92px]">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm font-heading font-semibold text-slate-800">Context</span>
              <button onClick={() => setCtxOpen(false)} data-testid="conv-ctx-collapse" aria-label="Collapse context panel" className="p-1 rounded hover:bg-slate-100 text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"><Icons.PanelRightClose className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[calc(100vh-11rem)] overflow-y-auto tf-scroll">
              <ContextBody c={c} meetings={meetings} tasks={tasks} recordings={recordings} uploaded={uploaded} wf={wf} estimates={estimates} tax={tax} onNavigate={onNavigate} />
            </div>
          </div>
        </aside>
      ) : (
        <button onClick={() => setCtxOpen(true)} data-testid="conv-ctx-expand" aria-label="Open context panel"
          className="hidden xl:flex flex-col items-center gap-1 shrink-0 self-start sticky top-[92px] bg-white border border-slate-200 rounded-xl shadow-sm p-2 text-slate-400 hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
          <Icons.PanelRightOpen className="h-4 w-4" />
        </button>
      )}

      {/* Mobile / tablet context drawer */}
      <Sheet open={ctxDrawerOpen} onOpenChange={setCtxDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-sm overflow-y-auto tf-scroll xl:hidden p-4" data-testid="conv-context-drawer">
          <SheetHeader><SheetTitle>Context</SheetTitle></SheetHeader>
          <div className="mt-2">
            <ContextBody c={c} meetings={meetings} tasks={tasks} recordings={recordings} uploaded={uploaded} wf={wf} estimates={estimates} tax={tax}
              onNavigate={(t) => { setCtxDrawerOpen(false); onNavigate?.(t); }} />
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
}
